import axios, { AxiosRequestConfig } from "axios";
import { AxiosError } from "axios";
import store from "@/store";
import NativeClient from "@/client/native-client";
import { API_CLIENT_CONST } from "@/const/api-const";
import { VERSION_INFORMATON } from "@/const/version-const";
import { ApiError, ApiFridgeError } from "@/types/common";

/** API実行時のサーバエラーが想定外の形式の場合に設定するデフォルトのエラー定義 */
const defaultApiError = {
  title: "ネットワークに接続できません",
  type: null,
  error: {
    message:
      "ネットワークに接続できません。スマートフォンがインターネットに接続できないようです。ネットワークの設定をご確認ください。",
    code: null
  }
};

/** レスポンスのエラー */
/** このメッセージは仮 */
const communicateApiError = {
  title: "",
  type: null,
  error: {
    message:
      "通信に失敗しました。",
    code: null
  }
};

/** API実行時の共通処理 */
const requestFunc = async (config: AxiosRequestConfig) => {
  try {
    // headers に Store で保持した Access Token, APIキーを付与
    const apiBaseParams = store.getters["common/getApiBaseParams"];
    NativeClient.sendDebugLog("API_CLIENT.ts requestFunc getApiParams completed");
    NativeClient.sendDebugLog("API_CLIENT.ts requestFunc config = " + JSON.stringify(config));
    if (apiBaseParams.token != null && apiBaseParams.token != "") {
      NativeClient.sendDebugLog("API_CLIENT.ts requestFunc token exist");
      return {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${apiBaseParams.token}`,
          "X-API-KEY": apiBaseParams.apiKey
        }
      };
    } else {
      // token未設定(ログアウト状態)でAPIを実行
      NativeClient.sendDebugLog("API_CLIENT.ts requestFunc token not exist");
      return {
        ...config,
        headers: {
          ...config.headers,
          "X-API-KEY": apiBaseParams.apiKey
        }
      };
    }
  } catch (error) {
    NativeClient.sendDebugLog("API_CLIENT.ts requestFunc catch");
    NativeClient.sendDebugLog("API_CLIENT.ts requestFunc catch error = " + error);
    NativeClient.sendDebugLog("API_CLIENT.ts requestFunc catch error config = " + JSON.stringify(config));
    return config;
  }
};

/** API実行エラー時の共通処理 */
const responseErrorFunc = (error: AxiosError) => {
  const { response } = error;
  const { request } = error;
  const { code } = error;

  // エラー発生時は、loadingを止める
  store.dispatch("common/stopLoading");
  NativeClient.sendDebugLog("responseErrorFunc start");

  if (response && response.status == 401) {
    // ステータスコード 401（アクセストークン切れ）の場合は、Nativeにトークンのリフレッシュ要求
    // エラー発生中の場合は、トークンのリフレッシュ要求を行わない（トークンの多重リフレッシュの抑止のため）
    const apiError = store.getters["common/getApiError"];
    if (!apiError) {
      NativeClient.getRefreshToken();
    }
  }

  // エラー時処理
  if (response && response.status > 0 && response.data) {
    // サーバエラーが想定内のフォーマットの場合
    // Native側で処理しないならば、エラーを保持する
    if (!NativeClient.setAPIErrorState(response.status, response.data)) {
      store.dispatch("common/updatedApiError", response.data);
    }
  } else {
    // サーバエラーが想定外のフォーマットの場合
    // デフォルトのエラー設定
    store.dispatch("common/updatedApiError", defaultApiError);
  }
  NativeClient.sendDebugLog("responseErrorFunc code = " + JSON.stringify(code));
  NativeClient.sendDebugLog("responseErrorFunc request = " + JSON.stringify(request));
  NativeClient.sendDebugLog("responseErrorFunc response = " + JSON.stringify(response));
  return Promise.reject(error);
};

/** API クライアント */
export const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  headers: {
    Accept: "application/json"
  }
});

/** 共通リクエスト */
apiClient.interceptors.request.use(requestFunc);

/** 共通エラーケース */
apiClient.interceptors.response.use(response => response, responseErrorFunc);

/**
 * 冷蔵庫API用
 */

/** API実行時の共通処理 */
const requestFridgeFunc = async (config: AxiosRequestConfig) => {
  try {
    // headers に Store で保持した Access Token, APIキーを付与
    const apiBaseParams = store.getters["common/getApiBaseParams"];
    NativeClient.sendDebugLog("requestFridgeFunc getApiParams completed");
    NativeClient.sendDebugLog("requestFridgeFunc config = " + JSON.stringify(config));
    const date = new Date();
    date.setTime(date.getTime() + 1000*60*60*9);// Convert to JST
    const dateString = date.toISOString().split('.')[0]
    if (apiBaseParams.token != null && apiBaseParams.token != "") {
      NativeClient.sendDebugLog("requestFridgeFunc token exist");
      return {
        ...config,
        headers: {
          ...config.headers,
          "content-type": "application/json;charset=UTF-8",
          "X-Reizo-Date": dateString,
          Authorization: `Bearer ${apiBaseParams.token}`,
        }
      };
    } else {
      // token未設定(ログアウト状態)でAPIを実行
      NativeClient.sendDebugLog("requestFridgeFunc token not exist");
      return {
        ...config,
        headers: {
          ...config.headers,
          "Content-Type": "application/json; charset=UTF-8",
          "X-Reizo-Date": dateString,
         }
      };
    }
  } catch (error) {
    NativeClient.sendDebugLog("requestFridgeFunc catch");
    NativeClient.sendDebugLog("requestFridgeFunc catch error = " + error);
    NativeClient.sendDebugLog("requestFridgeFunc catch error config = " + JSON.stringify(config));
    return config;
  }
};

/** API実行エラー時の共通処理 */
const responseFridgeErrorFunc = (error: AxiosError) => {
  const { response } = error;
  const { request } = error;
  const { code } = error;

  // エラー発生時は、loadingを止める
  store.dispatch("common/stopLoading");
  store.dispatch("common/stopFullscreenLoading");

  if (response && response.status == 401) {
    // ステータスコード 401（アクセストークン切れ）の場合は、Nativeにトークンのリフレッシュ要求
    // エラー発生中の場合は、トークンのリフレッシュ要求を行わない（トークンの多重リフレッシュの抑止のため）
    const apiError = store.getters["common/getApiError"];
    NativeClient.sendDebugLog("responseFridgeErrorFunc statusCode = 401");
    if (!apiError) {
      NativeClient.getRefreshToken();
    }
  }

  // エラー時処理
  if (response && response.status > 0 && response.data) {
    // サーバエラーが想定内のフォーマットの場合
    const fridgeErrorInfo: ApiFridgeError = response.data;
    if (fridgeErrorInfo.code == undefined || fridgeErrorInfo.message == undefined) {
      NativeClient.sendDebugLog("responseFridgeErrorFunc error message couldn't decode");
      // どのようなメッセージにするのか
      store.dispatch("common/updatedApiError", communicateApiError);
    } else {
      NativeClient.sendDebugLog("responseFridgeErrorFunc fridgeErrorInfo.message = " + fridgeErrorInfo.message + ", code = " + fridgeErrorInfo.code);
      const errorInfo: ApiError = { type: null, title: null, error: { message: fridgeErrorInfo.message, code: fridgeErrorInfo.code }};
      store.dispatch("common/updatedApiError", errorInfo);
    }
    NativeClient.sendDebugLog("responseFridgeErrorFunc response code = " + response.status + ", body = " + JSON.stringify(response.data));
  } else {
    // サーバエラーが想定外のフォーマットの場合
    // デフォルトのエラー設定
    store.dispatch("common/updatedApiError", defaultApiError);
    if (response === undefined) {
      NativeClient.sendDebugLog("responseFridgeErrorFunc response undefined");
    } else {
      NativeClient.sendDebugLog("responseFridgeErrorFunc response code = " + response.status + ", body = " + JSON.stringify(response.data));
    }
  }
  NativeClient.sendDebugLog("responseFridgeErrorFunc code = " + JSON.stringify(code));
  NativeClient.sendDebugLog("responseFridgeErrorFunc request = " + JSON.stringify(request));
  NativeClient.sendDebugLog("responseFridgeErrorFunc response = " + JSON.stringify(response));
  return Promise.reject(error);
};

/** API クライアント */
export const apiFridgeClient = axios.create({
  baseURL: process.env.VUE_APP_FRIDGE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json; charset=UTF-8',
    Accept: "application/json",
  },
  timeout: 60000
});

/** 共通リクエスト */
apiFridgeClient.interceptors.request.use(requestFridgeFunc);

/** 共通エラーケース */
apiFridgeClient.interceptors.response.use(response => response, responseFridgeErrorFunc);

// ApplianceIdのAPI用に変換する
export const translateApplianceId = (originalApplianceId: string | undefined | null) => {
  let result = "";
  if (originalApplianceId != undefined && originalApplianceId != null) {
    result = encodeURI(originalApplianceId.replace("/", "_"));
  }
  return result;
};

// ApplianceId、termIdの文字列をAPI用に変換する
export const translateFridgeParameter = (original: string | undefined | null) => {
  let result = "";
  if (original != undefined && original != null) {
    // 正規表現のreplace(/+/g, "-")ではエラーになるので、以下のように実装 
    result = encodeURIComponent(original.split("/").join("_").split("+").join("-"));
  }
  return result;
};

export default apiClient;
